import GitHubIcon from "@mui/icons-material/GitHub";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import { Box, Container, Link, List, ListItem, ListItemText, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React from "react";
import { useTranslation } from "react-i18next";
import PageTitle from "../../components/Common/PageTitle";
import contactInfo from "../../data/contactInfo.json";
import { cardContainer, imageStyle, listItemStyle, paragraphIndent, socialContainer, socialIcon, specialFont } from "../../styles/commonStyles";

function AboutPage() {
  const { t } = useTranslation("about");
  const theme = useTheme();

  const FeatureList = ({ title, features }) => (
    <Box>
      <Typography variant="h5" component="h2" sx={{ pl: 1.5 }} gutterBottom>
        {title}
      </Typography>
      <List>
        {features.map((feature, index) => (
          <ListItem key={index} sx={listItemStyle}>
            <Typography variant="body1" fontWeight="bold">
              {feature.title}
            </Typography>
            <Typography variant="body2" sx={{ ml: 0.5 }}>
              {feature.description}
            </Typography>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  const SocialLinks = () => {
    const theme = useTheme();
    return (
      <Box sx={socialContainer(theme)}>
        <Typography variant="h6" gutterBottom sx={{ color: theme.palette.secondary.contrastText }}>
          {t("connect_with_us")}
        </Typography>
        <Box display="flex" justifyContent="center" gap={3}>
          <Link href={contactInfo.github} target="_blank" rel="noopener noreferrer" sx={socialIcon(theme)}>
            <GitHubIcon fontSize="large" />
          </Link>
          <Link href={contactInfo.linkedin} target="_blank" rel="noopener noreferrer" sx={socialIcon(theme)}>
            <LinkedInIcon fontSize="large" />
          </Link>
        </Box>
      </Box>
    );
  };

  return (
    <Container>
      {/* Introduction Section */}
      <Box sx={cardContainer(theme)}>
        <PageTitle text={t("title")} sx={specialFont(theme)} />
        <Typography variant="body1" paragraph>
          {t("intro")}
        </Typography>
      </Box>
      <br></br>
      {/* Platform Features */}
      <FeatureList title={t("platform_title")} features={t("platform_features", { returnObjects: true })} />

      {/* Creator Story */}
      <Box sx={cardContainer(theme)}>
        <Box sx={{ display: "flex", alignItems: "flex-start", flexDirection: { xs: "column", md: "row" }, gap: theme.spacing(3) }}>
          {/* Text Content */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" component="h2" gtterBottom>
              {t("creator_title")}
            </Typography>
            <Typography variant="body1">{t("creator_story1")}</Typography>
            <Typography variant="body1">{t("creator_story2")}</Typography>
            <Typography variant="body1">{t("creator_story3")}</Typography>
          </Box>
          {/* Image */}

          <Box component="img" src="assets/images/IMG_3089.JPG" alt={t("hackathon_image1_alt")} sx={imageStyle(theme)} />
        </Box>
      </Box>
      <br></br>

      {/* Hackathon Experience */}
      <Box sx={{ marginBottom: theme.spacing(3) }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", flexDirection: { xs: "column", md: "row" }, gap: theme.spacing(3) }}>
          {/* Text */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" component="h3" gutterBottom sx={paragraphIndent(theme)}>
              {t("hackathon_experience_title")}
            </Typography>
            <Typography variant="body1" sx={paragraphIndent(theme)}>
              {t("hackathon_experience")}
            </Typography>
          </Box>

          {/* Image */}
          <Box component="img" src="assets/images/IMG_3094.JPG" alt={t("hackathon_image2_alt")} sx={imageStyle(theme)} />
        </Box>
      </Box>

      {/* Mission & Goals */}
      <Box>
        <Typography variant="h5" component="h2" sx={paragraphIndent(theme)} gutterBottom>
          {t("mission_title")}
        </Typography>
        <List>
          {t("mission_points", { returnObjects: true }).map((point, index) => (
            <ListItem key={index} sx={listItemStyle}>
              <ListItemText primary={<span dangerouslySetInnerHTML={{ __html: point }} />} />
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Invitation & Contact Info */}
      <Box sx={{ mt: theme.spacing(3) }}>
        <Typography variant="h6" gutterBottom>
          {t("invitation")}
        </Typography>
        <Typography variant="body1" paragraph>
          {t("current_status")}
        </Typography>
        <Typography
          variant="body1"
          dangerouslySetInnerHTML={{
            __html: t("contact_info", { email: contactInfo.personalEmail }),
          }}
        />
      </Box>

      {/* Social Links */}
      <SocialLinks />
    </Container>
  );
}

export default AboutPage;
